import { useRef, useState } from 'react';
import BulkTableModal from '../../components/form/bulk-table/BulkTableModal';
import { downloadScsPanelReport } from './scs-panel-report';
import { downloadScsCompletionReport, type CompletionReportForm } from './scs-completion-report';

type Props = {
  year: string;
  quarter: string;
  years: string[];
  quartersForYear: (year: string) => string[];
  formIdsForPeriod: (year: string, quarter: string) => string[];
  completionForms: CompletionReportForm[];
  token: () => Promise<string>;
};

export default function ScsReports({
  year,
  quarter,
  years,
  quartersForYear,
  formIdsForPeriod,
  completionForms,
  token,
}: Props) {
  const [open, setOpen] = useState(false);
  const [reportYear, setReportYear] = useState(year);
  const [reportQuarter, setReportQuarter] = useState(quarter);
  const [busy, setBusy] = useState(false);
  const [reportType, setReportType] = useState('moderation');
  const running = useRef(false);
  const downloadButton = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState('');
  const quarters = quartersForYear(reportYear);
  const ids = (reportYear && reportQuarter ? formIdsForPeriod(reportYear, reportQuarter) : [])
    .filter((id) => reportType === 'completion'
      || !completionForms.some((form) => form.formId === id && form.currentStatus === 'On Hold'));
  const close = () => {
    if (!running.current) setOpen(false);
  };
  const download = async () => {
    if (running.current || !ids.length) return;
    running.current = true;
    setBusy(true);
    setError('');
    try {
      if (reportType === 'completion') {
        await downloadScsCompletionReport(await token(), ids, completionForms, reportYear);
      } else {
        await downloadScsPanelReport(await token(), ids, reportYear, reportQuarter);
      }
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to download report.');
    } finally {
      running.current = false;
      setBusy(false);
      requestAnimationFrame(() => downloadButton.current?.focus());
    }
  };
  return (
    <>
      <button
        type="button"
        className="govuk-button govuk-button--secondary"
        onClick={() => {
          setReportYear(year);
          setReportQuarter(quarter);
          setReportType('moderation');
          setError('');
          setOpen(true);
        }}
      >
        Reports
      </button>
      {open && (
        <BulkTableModal title="Reports" onClose={close}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void download();
            }}
          >
            {error && (
              <p className="govuk-error-message" role="alert">
                {error}
              </p>
            )}
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="scs-reports-year">
                Reporting Year
              </label>
              <select
                className="govuk-select"
                id="scs-reports-year"
                value={reportYear}
                disabled={busy}
                onChange={(event) => {
                  const value = event.target.value;
                  setReportYear(value);
                  if (!quartersForYear(value).includes(reportQuarter)) setReportQuarter('');
                }}
              >
                <option value="">Select year</option>
                {years.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="scs-reports-quarter">
                Reporting Quarter
              </label>
              <select
                className="govuk-select"
                id="scs-reports-quarter"
                value={reportQuarter}
                disabled={busy}
                onChange={(event) => setReportQuarter(event.target.value)}
              >
                <option value="">Select quarter</option>
                {quarters.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="scs-reports-type">
                Report
              </label>
              <select
                className="govuk-select"
                id="scs-reports-type"
                disabled={busy}
                value={reportType}
                onChange={(event) => setReportType(event.target.value)}
              >
                <option value="moderation">Moderation report</option>
                <option value="completion">Completion Report</option>
              </select>
            </div>
            {reportYear && reportQuarter && !ids.length && (
              <p className="govuk-body" role="status">
                No responses match these filters.
              </p>
            )}
            {busy && (
              <p className="govuk-body" role="status">
                Preparing report...
              </p>
            )}
            <div className="govuk-button-group">
              <button
                ref={downloadButton}
                className="govuk-button"
                type="submit"
                disabled={busy || !ids.length}
              >
                Download Report
              </button>
              <button
                className="govuk-button govuk-button--secondary"
                type="button"
                onClick={close}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </form>
        </BulkTableModal>
      )}
    </>
  );
}
